import { Controller } from "@hotwired/stimulus"

// Un <select> long qu'on peut CHERCHER (Michael, 2026-09-20).
//
// Le plan comptable fait plusieurs centaines de lignes et la liste des
// événements grandit à chaque saison : dérouler un <select> natif pour y
// trouver « 700100 Ventes bar » revient à lire l'annuaire. Ce contrôleur pose
// un champ de recherche par-dessus, filtre à la frappe, et se pilote au
// clavier.
//
// LE <select> RESTE LA SOURCE DE VÉRITÉ. Il est masqué quand JS est actif,
// jamais retiré : c'est lui que le formulaire soumet, et sans JS il reste
// utilisable tel quel — même dégradation gracieuse que `customer-search`.
//
// La recherche ignore les accents ET la casse : « evenement » trouve
// « Événement », « epicerie » trouve « Épicerie ». Sans ça, il faut taper
// juste pour trouver, ce qui est exactement ce qu'on essaie d'éviter.
//
// Usage (dans une vue) :
//   div(data-controller="searchable-select" class="relative")
//     = f.collection_select …, data: { searchable_select_target: "select" }
//     div(data-searchable-select-target="wrap" class="hidden")
//       input(data-searchable-select-target="input"
//             data-action="input->searchable-select#filter focus->searchable-select#open keydown->searchable-select#navigate")
//       ul(data-searchable-select-target="list" class="hidden")
export default class extends Controller {
  static targets = ["select", "wrap", "input", "list"]
  // `clearable` : un champ facultatif (le pôle d'une ligne) doit pouvoir
  // revenir à vide. Vider le texte puis quitter le champ remet alors le
  // <select> sur son option vide, au lieu de rétablir l'ancien choix.
  static values = {
    placeholder: { type: String, default: "Chercher…" },
    max: { type: Number, default: 50 },
    clearable: { type: Boolean, default: false }
  }

  connect() {
    this.lireLesOptions()

    this.selectTarget.classList.add("hidden")
    this.wrapTarget.classList.remove("hidden")
    // Un <select> obligatoire masqué bloquerait l'envoi EN SILENCE : le
    // navigateur refuse le formulaire mais ne peut pas montrer un champ caché
    // (« An invalid form control is not focusable »). L'obligation passe donc
    // au champ de recherche, qui n'est rempli que si une option est retenue.
    if (this.selectTarget.required) {
      this.selectTarget.required = false
      this.inputTarget.required = true
    }
    this.inputTarget.placeholder = this.placeholderValue
    this.actif = -1
    this.refleterLaSelection()

    this.fermerAuClicDehors = (event) => {
      if (!this.element.contains(event.target)) this.close()
    }
    document.addEventListener("click", this.fermerAuClicDehors)

    // Un autre contrôleur peut changer le <select> (un fournisseur créé depuis
    // l'écran s'y ajoute et s'y sélectionne) : le champ doit le montrer.
    this.suivreLeSelect = () => {
      this.lireLesOptions()
      this.refleterLaSelection()
    }
    this.selectTarget.addEventListener("change", this.suivreLeSelect)

    // La liste suit son champ quand la page (ou un bloc qui défile) bouge
    // pendant qu'elle est ouverte. Un défilement DANS la liste ne la déplace pas.
    this.suivreLeChamp = (event) => {
      if (event.target !== this.listTarget) this.placer()
    }
  }

  disconnect() {
    document.removeEventListener("click", this.fermerAuClicDehors)
    this.selectTarget.removeEventListener("change", this.suivreLeSelect)
    this.cesserDeSuivre()
  }

  // Relue à chaque recherche : la liste peut grandir pendant qu'on est sur
  // l'écran, et quelques centaines d'options se relisent en un rien de temps.
  lireLesOptions() {
    this.options = Array.from(this.selectTarget.options).map((option, index) => ({
      index,
      value: option.value,
      label: option.text.trim(),
      cle: this.constructor.normaliser(option.text)
    }))
  }

  // Le libellé de l'option retenue, ou rien si c'est l'option vide : le
  // placeholder dit alors ce qu'on attend, ce qu'un « Choisir… » écrit en dur
  // dans le champ ne ferait pas — on le prendrait pour une valeur.
  refleterLaSelection() {
    const choisie = this.options.find((o) => o.value === this.selectTarget.value)
    this.inputTarget.value = choisie && choisie.value !== "" ? choisie.label : ""
  }

  open() {
    this.filter()
  }

  close() {
    this.listTarget.classList.add("hidden")
    this.cesserDeSuivre()
    this.inputTarget.setAttribute("aria-expanded", "false")
    this.actif = -1
    if (this.clearableValue && this.inputTarget.value.trim() === "" && this.selectTarget.value !== "") {
      const vide = Array.from(this.selectTarget.options).findIndex((o) => o.value === "")
      if (vide >= 0) {
        this.selectTarget.selectedIndex = vide
        this.selectTarget.dispatchEvent(new Event("change", { bubbles: true }))
      }
    }
    // Le champ ne garde jamais un texte qui ne correspond à rien : sinon on
    // croit avoir choisi « Ventes bar » alors que le <select> est resté vide.
    this.refleterLaSelection()
  }

  filter() {
    this.lireLesOptions()
    // Le champ montre le choix en cours : tant qu'on n'a rien tapé d'autre, la
    // liste propose TOUTES les options, pas seulement celle déjà retenue.
    const choisie = this.options.find((o) => o.value === this.selectTarget.value)
    const texte = choisie && this.inputTarget.value === choisie.label ? "" : this.inputTarget.value
    const requete = this.constructor.normaliser(texte)
    const visibles = this.options
      .filter((o) => o.value !== "" && (requete === "" || o.cle.includes(requete)))
      .slice(0, this.maxValue)

    this.visibles = visibles
    this.actif = visibles.length ? 0 : -1
    this.dessiner()
  }

  dessiner() {
    if (!this.visibles.length) {
      this.listTarget.innerHTML =
        '<li class="px-3 py-2 text-sm text-gray-500">Aucun résultat</li>'
    } else {
      this.listTarget.innerHTML = this.visibles
        .map(
          (o, rang) =>
            `<li role="option" id="${this.identifiant(rang)}" data-index="${o.index}" data-action="mousedown->searchable-select#choisirDepuisLaListe" aria-selected="${rang === this.actif}" class="cursor-pointer px-3 py-2 text-sm ${rang === this.actif ? "bg-gray-100 text-gray-900" : "text-gray-700"}">${this.constructor.echapper(o.label)}</li>`
        )
        .join("")
    }
    this.listTarget.classList.remove("hidden")
    this.inputTarget.setAttribute("aria-expanded", "true")
    this.placer()
    if (!this.suivi) {
      window.addEventListener("scroll", this.suivreLeChamp, { capture: true, passive: true })
      window.addEventListener("resize", this.suivreLeChamp)
      this.suivi = true
    }
    this.marquerActif()
  }

  // La liste est posée en `position: fixed` sous son champ (Michael,
  // 2026-10-19). En `absolute`, elle restait prisonnière du premier bloc
  // `overflow-hidden` (les cartes arrondies de la file « À affecter ») et se
  // faisait couper au bas de la carte. Fixée sur l'écran, elle passe
  // par-dessus, et s'ouvre vers le haut quand la place manque en dessous.
  //
  // La largeur reste celle que la vue lui donne (`w-full` du champ, `w-64`…) :
  // elle est mesurée en `absolute`, avant de passer en `fixed`.
  placer() {
    const liste = this.listTarget
    if (liste.classList.contains("hidden")) return

    liste.style.position = ""
    liste.style.top = ""
    liste.style.left = ""
    liste.style.width = ""
    liste.style.maxHeight = ""
    liste.style.marginTop = ""
    const largeur = liste.offsetWidth
    const hauteurMax = parseFloat(getComputedStyle(liste).maxHeight) || 256

    const champ = this.inputTarget.getBoundingClientRect()
    const marge = 8
    const ecart = 4
    const dessous = window.innerHeight - champ.bottom - ecart - marge
    const dessus = champ.top - ecart - marge
    const voulue = Math.min(liste.scrollHeight, hauteurMax)
    const versLeHaut = dessous < voulue && dessus > dessous

    liste.style.position = "fixed"
    liste.style.marginTop = "0"
    liste.style.width = `${largeur}px`
    liste.style.maxHeight = `${Math.max(Math.min(hauteurMax, versLeHaut ? dessus : dessous), 96)}px`

    const gauche = Math.max(marge, Math.min(champ.left, window.innerWidth - marge - largeur))
    const haut = versLeHaut ? champ.top - ecart - liste.offsetHeight : champ.bottom + ecart
    liste.style.left = `${gauche}px`
    liste.style.top = `${haut}px`

    // Un ancêtre transformé (une modale animée) sert de repère au `fixed` à la
    // place de l'écran : on corrige du décalage constaté.
    const pose = liste.getBoundingClientRect()
    if (Math.abs(pose.left - gauche) > 0.5) liste.style.left = `${2 * gauche - pose.left}px`
    if (Math.abs(pose.top - haut) > 0.5) liste.style.top = `${2 * haut - pose.top}px`
  }

  cesserDeSuivre() {
    if (!this.suivi) return
    window.removeEventListener("scroll", this.suivreLeChamp, { capture: true })
    window.removeEventListener("resize", this.suivreLeChamp)
    this.suivi = false
  }

  navigate(event) {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault()
      if (this.listTarget.classList.contains("hidden")) return this.filter()
      const pas = event.key === "ArrowDown" ? 1 : -1
      this.actif = Math.min(Math.max(this.actif + pas, 0), this.visibles.length - 1)
      this.marquerActif()
    } else if (event.key === "Enter") {
      if (this.listTarget.classList.contains("hidden")) return
      event.preventDefault()
      if (this.actif >= 0) this.choisir(this.visibles[this.actif].index)
    } else if (event.key === "Escape") {
      this.close()
    }
  }

  choisirDepuisLaListe(event) {
    // `mousedown` et pas `click` : le `blur` du champ part avant le clic et
    // refermerait la liste sous le curseur.
    event.preventDefault()
    this.choisir(Number(event.currentTarget.dataset.index))
  }

  choisir(index) {
    this.selectTarget.selectedIndex = index
    // Les écrans qui écoutent le <select> (un `auto-submit`, un calcul de
    // total) doivent voir passer le changement comme si un humain l'avait fait.
    this.selectTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.close()
  }

  marquerActif() {
    Array.from(this.listTarget.children).forEach((li, rang) => {
      const actif = rang === this.actif
      li.setAttribute("aria-selected", actif ? "true" : "false")
      li.classList.toggle("bg-gray-100", actif)
      li.classList.toggle("text-gray-900", actif)
      li.classList.toggle("text-gray-700", !actif)
      if (actif) this.montrerDansLaListe(li)
    })
    const courant = this.actif >= 0 ? this.identifiant(this.actif) : ""
    this.inputTarget.setAttribute("aria-activedescendant", courant)
  }

  // Défile la liste seule : `scrollIntoView` faisait aussi défiler la page, ou
  // le bloc qui contient le champ.
  montrerDansLaListe(li) {
    const liste = this.listTarget
    const haut = li.offsetTop
    const bas = haut + li.offsetHeight
    if (haut < liste.scrollTop) liste.scrollTop = haut
    else if (bas > liste.scrollTop + liste.clientHeight) liste.scrollTop = bas - liste.clientHeight
  }

  identifiant(rang) {
    return `${this.element.id || this.selectTarget.name.replace(/\W+/g, "-")}-option-${rang}`
  }

  static normaliser(texte) {
    return texte
      .toString()
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .toLowerCase()
      .trim()
  }

  static echapper(texte) {
    const div = document.createElement("div")
    div.textContent = texte
    return div.innerHTML
  }
}
